import React, { useState, useEffect, useMemo, useCallback } from "react";
import type { LearningModule, Batch, BatchStepDate, Step } from "./types";
import BatchMultiSelect from "../BatchMultiSelect";
import ApiService from "../../_services/ApiService";

type BatchesTabProps = {
  module: LearningModule | null;
  onModuleChange: (updates: Partial<LearningModule>) => void;
  isViewMode: boolean;
};

type StepDateMap = Record<number, BatchStepDate>; // keyed by stepId

// Date format helpers: internal YYYY-MM-DD <-> display DD-MM-YYYY
const toDisplay = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const m = iso.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};
const toISO = (display: string): string | null => {
  const m = display.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
};

export default function BatchesTab({ module, onModuleChange, isViewMode }: BatchesTabProps) {
  // Batch objects for display
  const [batchObjects, setBatchObjects] = useState<Batch[]>([]);
  // Currently selected batch in the schedule dropdown
  const [activeBatchId, setActiveBatchId] = useState<number | null>(null);
  // Step dates per batch: batchId -> { stepId -> dates }
  const [batchDatesMap, setBatchDatesMap] = useState<Record<number, StepDateMap>>({});
  // Loading/saving states
  const [loadingDates, setLoadingDates] = useState(false);
  const [savingDates, setSavingDates] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  // Shift dates popover
  const [showShiftPopover, setShowShiftPopover] = useState(false);
  const [shiftDays, setShiftDays] = useState<number>(7);
  // Copy-from source: batch ID, "template", or empty
  const [copyFromSource, setCopyFromSource] = useState<number | "template" | "">("");
  // Dirty tracking for per-batch dates
  const [dirtyBatches, setDirtyBatches] = useState<Set<number>>(new Set());

  // Flatten all steps from all groups
  const allSteps = useMemo(() => {
    if (!module) return [];
    const steps: Array<{ step: Step; chapterName: string; chapterColor: string; chapterIndex: number; stepIndex: number }> = [];
    module.groups.forEach((group, gi) => {
      group.steps.forEach((step, si) => {
        steps.push({
          step,
          chapterName: group.name,
          chapterColor: group.color,
          chapterIndex: gi,
          stepIndex: si,
        });
      });
    });
    return steps;
  }, [module]);

  // Load batch objects when selectedBatches changes
  useEffect(() => {
    const loadBatches = async () => {
      if (!module?.selectedBatches || module.selectedBatches.length === 0) {
        setBatchObjects([]);
        setActiveBatchId(null);
        return;
      }
      try {
        const response = await ApiService.getBatchesByIds(module.selectedBatches);
        const responseData = response.data as any;
        const batches = responseData?.data || responseData || [];
        const batchArr = Array.isArray(batches) ? batches : [];
        setBatchObjects(batchArr);
        // Auto-select first batch if none selected
        if (!activeBatchId || !module.selectedBatches.includes(activeBatchId)) {
          setActiveBatchId(batchArr.length > 0 ? batchArr[0].id : null);
        }
      } catch (error) {
        console.error("[BatchesTab] Error fetching batch details:", error);
        const fallback = module.selectedBatches.map((id) => ({ id, name: `Batch ${id}` }));
        setBatchObjects(fallback);
        if (!activeBatchId) setActiveBatchId(fallback[0]?.id || null);
      }
    };
    loadBatches();
  }, [module?.selectedBatches]);

  // Load step dates from backend when module loads
  useEffect(() => {
    if (!module?.id) return;
    const loadDates = async () => {
      setLoadingDates(true);
      try {
        const response = await ApiService.getBatchStepDates(module.id!);
        const data = (response.data as any)?.data || response.data || [];
        // data is array of { batchId, stepId, plannedStartDate, plannedEndDate, isDefault }
        const map: Record<number, StepDateMap> = {};
        // Track which batches have only default (unsaved) dates
        const batchesWithDefaults = new Set<number>();
        if (Array.isArray(data)) {
          data.forEach((row: any) => {
            if (!map[row.batchId]) map[row.batchId] = {};
            map[row.batchId][row.stepId] = {
              stepId: row.stepId,
              plannedStartDate: row.plannedStartDate || null,
              plannedEndDate: row.plannedEndDate || null,
            };
            // If this row is a default (not saved in lesson_plan_step_status), track it
            if (row.isDefault === 1 || row.isDefault === true) {
              batchesWithDefaults.add(row.batchId);
            }
          });
        }
        setBatchDatesMap(map);

        // Mark batches with default (unsaved) dates as dirty so Save Schedule button is enabled
        if (batchesWithDefaults.size > 0) {
          setDirtyBatches((prev) => {
            const next = new Set(prev);
            batchesWithDefaults.forEach((bid) => next.add(bid));
            return next;
          });
        }
      } catch (error) {
        console.error("[BatchesTab] Error loading batch step dates:", error);
      } finally {
        setLoadingDates(false);
      }
    };
    loadDates();
  }, [module?.id]);

  // Handle batch selection changes
  const handleBatchesChange = (batches: Batch[]) => {
    if (!module) return;
    onModuleChange({ selectedBatches: batches.map((b) => b.id) });
  };

  // Get current dates for active batch
  const activeBatchDates = useMemo(() => {
    if (!activeBatchId) return {};
    return batchDatesMap[activeBatchId] || {};
  }, [activeBatchId, batchDatesMap]);

  // Update a single step date for the active batch
  const updateStepDate = useCallback(
    (stepId: number, field: "plannedStartDate" | "plannedEndDate", value: string | null) => {
      if (!activeBatchId) return;
      setBatchDatesMap((prev) => {
        const batchDates = { ...(prev[activeBatchId] || {}) };
        const existing = batchDates[stepId] || { stepId, plannedStartDate: null, plannedEndDate: null };
        batchDates[stepId] = { ...existing, [field]: value || null };
        return { ...prev, [activeBatchId]: batchDates };
      });
      setDirtyBatches((prev) => new Set(prev).add(activeBatchId));
    },
    [activeBatchId]
  );

  // Count how many steps have dates for a batch
  const getDateCount = useCallback(
    (batchId: number) => {
      const dates = batchDatesMap[batchId] || {};
      let count = 0;
      Object.values(dates).forEach((d) => {
        if (d.plannedStartDate || d.plannedEndDate) count++;
      });
      return count;
    },
    [batchDatesMap]
  );

  const totalSteps = allSteps.length;

  // Calculate days between two dates (inclusive — same day = 1 day)
  const calcDays = (start: string | null, end: string | null): number | null => {
    if (!start || !end) return null;
    const diff = new Date(end).getTime() - new Date(start).getTime();
    return Math.round(diff / (1000 * 60 * 60 * 24)) + 1;
  };

  // Copy dates from another batch or from template
  const handleCopyFrom = () => {
    if (!activeBatchId || !copyFromSource) return;

    let sourceDates: StepDateMap;
    if (copyFromSource === "template") {
      // Build dates from step template dates
      sourceDates = {};
      allSteps.forEach(({ step }) => {
        if (step.id) {
          sourceDates[step.id] = {
            stepId: step.id,
            plannedStartDate: step.startDate ? step.startDate.slice(0, 10) : null,
            plannedEndDate: step.endDate ? step.endDate.slice(0, 10) : null,
          };
        }
      });
    } else {
      sourceDates = batchDatesMap[Number(copyFromSource)];
      if (!sourceDates) return;
    }

    setBatchDatesMap((prev) => ({
      ...prev,
      [activeBatchId]: { ...sourceDates },
    }));
    setDirtyBatches((prev) => new Set(prev).add(activeBatchId));
    setCopyFromSource("");
  };

  // Shift all dates for active batch by N days
  const handleShiftDates = () => {
    if (!activeBatchId || shiftDays === 0) return;
    setBatchDatesMap((prev) => {
      const currentDates = { ...(prev[activeBatchId] || {}) };
      const shifted: StepDateMap = {};
      Object.entries(currentDates).forEach(([stepIdStr, dateRow]) => {
        const stepId = Number(stepIdStr);
        shifted[stepId] = {
          stepId,
          plannedStartDate: shiftDate(dateRow.plannedStartDate, shiftDays),
          plannedEndDate: shiftDate(dateRow.plannedEndDate, shiftDays),
        };
      });
      return { ...prev, [activeBatchId]: shifted };
    });
    setDirtyBatches((prev) => new Set(prev).add(activeBatchId));
    setShowShiftPopover(false);
  };

  // Save dates for active batch
  const handleSave = async () => {
    if (!module?.id || !activeBatchId) return;
    const dates = batchDatesMap[activeBatchId] || {};
    const steps = Object.values(dates).filter((d) => d.plannedStartDate || d.plannedEndDate);

    setSavingDates(true);
    setSaveMessage(null);
    try {
      await ApiService.saveBatchStepDates(module.id, activeBatchId, steps);
      setDirtyBatches((prev) => {
        const next = new Set(prev);
        next.delete(activeBatchId);
        return next;
      });
      setSaveMessage({ type: "success", text: "Schedule saved successfully" });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: any) {
      console.error("[BatchesTab] Error saving batch step dates:", error);
      setSaveMessage({ type: "error", text: error?.message || "Failed to save schedule" });
    } finally {
      setSavingDates(false);
    }
  };

  // Get batch display name
  const getBatchName = (batch: Batch) => batch.displayName || batch.name || `Batch ${batch.id}`;

  // Track which chapter we last rendered to show chapter headers
  let lastChapterIndex = -1;

  return (
    <div className="lmb-batches-tab">
      {/* Batch Selection Card */}
      <div className="lmb-card">
        <div className="lmb-card-hd">
          <div><strong>👥 Assigned Batches</strong></div>
          {batchObjects.length > 0 && (
            <span className="lmb-muted">{batchObjects.length} batch{batchObjects.length !== 1 ? "es" : ""}</span>
          )}
        </div>
        <div className="lmb-card-bd">
          <BatchMultiSelect
            selectedBatches={batchObjects}
            onChange={handleBatchesChange}
            placeholder="Search and add batches..."
            disabled={isViewMode}
          />
        </div>
      </div>

      {/* Step Schedule Card */}
      {batchObjects.length > 0 && allSteps.length > 0 && (
        <div className="lmb-card">
          <div className="lmb-card-hd">
            <div><strong>📅 Step Schedule</strong></div>
          </div>
          <div className="lmb-card-bd">
            {/* Toolbar: batch selector + actions */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              {/* Batch selector */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label className="lmb-muted" style={{ whiteSpace: "nowrap" }}>Batch:</label>
                <select
                  className="lmb-select"
                  value={activeBatchId || ""}
                  onChange={(e) => setActiveBatchId(Number(e.target.value))}
                  style={{ minWidth: 200 }}
                >
                  {batchObjects.map((b) => (
                    <option key={b.id} value={b.id}>
                      {getBatchName(b)} ({getDateCount(b.id)}/{totalSteps})
                    </option>
                  ))}
                </select>
              </div>

              {/* Copy from */}
              {!isViewMode && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <label className="lmb-muted" style={{ whiteSpace: "nowrap" }}>Copy from:</label>
                  <select
                    className="lmb-select"
                    value={copyFromSource}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCopyFromSource(val === "" ? "" : val === "template" ? "template" : Number(val));
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <option value="">Select source...</option>
                    <option value="template">Template dates</option>
                    {batchObjects
                      .filter((b) => b.id !== activeBatchId)
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {getBatchName(b)} ({getDateCount(b.id)}/{totalSteps})
                        </option>
                      ))}
                  </select>
                  <button
                    className="lmb-btn lmb-btn-sm"
                    onClick={handleCopyFrom}
                    disabled={!copyFromSource}
                    type="button"
                  >
                    Copy
                  </button>
                </div>
              )}

              {/* Shift dates */}
              {!isViewMode && (
                <div style={{ position: "relative" }}>
                  <button
                    className="lmb-btn lmb-btn-sm"
                    onClick={() => setShowShiftPopover(!showShiftPopover)}
                    type="button"
                  >
                    Shift dates
                  </button>
                  {showShiftPopover && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        marginTop: 4,
                        padding: "12px 16px",
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span className="lmb-muted">Shift by</span>
                      <input
                        type="number"
                        className="lmb-input"
                        value={shiftDays}
                        onChange={(e) => setShiftDays(Number(e.target.value))}
                        style={{ width: 70, textAlign: "center" }}
                      />
                      <span className="lmb-muted">days</span>
                      <button className="lmb-btn lmb-btn-sm lmb-btn-primary" onClick={handleShiftDates} type="button">
                        Apply
                      </button>
                      <button
                        className="lmb-btn lmb-btn-sm"
                        onClick={() => setShowShiftPopover(false)}
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Loading indicator */}
            {loadingDates && (
              <div className="lmb-muted" style={{ textAlign: "center", padding: 16 }}>
                Loading schedule...
              </div>
            )}

            {/* Step date table */}
            {!loadingDates && activeBatchId && (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border-default)" }}>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>
                        Step
                      </th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 13, color: "var(--text-muted)", fontWeight: 600, width: 160 }}>
                        Start Date
                      </th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 13, color: "var(--text-muted)", fontWeight: 600, width: 160 }}>
                        End Date
                      </th>
                      <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 13, color: "var(--text-muted)", fontWeight: 600, width: 60 }}>
                        Days
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allSteps.map(({ step, chapterName, chapterColor, chapterIndex, stepIndex }) => {
                      const showChapterHeader = chapterIndex !== lastChapterIndex;
                      lastChapterIndex = chapterIndex;
                      const stepId = step.id;
                      const dates = stepId ? activeBatchDates[stepId] : undefined;
                      const days = calcDays(dates?.plannedStartDate || null, dates?.plannedEndDate || null);

                      return (
                        <React.Fragment key={`${chapterIndex}-${stepIndex}`}>
                          {showChapterHeader && (
                            <tr style={{ background: "var(--surface-page)" }}>
                              <td colSpan={4} style={{ padding: "8px 12px", fontWeight: 600, fontSize: 13 }}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: chapterColor || "#6b7280",
                                    marginRight: 8,
                                    verticalAlign: "middle",
                                  }}
                                />
                                {chapterName}
                              </td>
                            </tr>
                          )}
                          <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                            <td style={{ padding: "8px 12px 8px 32px", fontSize: 13 }}>
                              {step.name || `Step ${stepIndex + 1}`}
                            </td>
                            <td style={{ padding: "6px 12px" }}>
                              {stepId ? (
                                <input
                                  type="text"
                                  className="lmb-input"
                                  placeholder="DD-MM-YYYY"
                                  value={toDisplay(dates?.plannedStartDate)}
                                  onChange={(e) => {
                                    let v = e.target.value.replace(/[^0-9-]/g, "");
                                    // Auto-insert dashes after DD and MM
                                    const digits = v.replace(/-/g, "");
                                    if (digits.length >= 4) v = digits.slice(0, 2) + "-" + digits.slice(2, 4) + "-" + digits.slice(4, 8);
                                    else if (digits.length >= 2) v = digits.slice(0, 2) + "-" + digits.slice(2);
                                    else v = digits;
                                    e.target.value = v;
                                    const iso = toISO(v);
                                    if (iso) updateStepDate(stepId, "plannedStartDate", iso);
                                    else if (!v) updateStepDate(stepId, "plannedStartDate", null);
                                  }}
                                  maxLength={10}
                                  disabled={isViewMode}
                                  style={{ fontSize: 13, padding: "4px 8px", width: 110 }}
                                />
                              ) : (
                                <span className="lmb-muted" style={{ fontSize: 12 }}>Save module first</span>
                              )}
                            </td>
                            <td style={{ padding: "6px 12px" }}>
                              {stepId ? (
                                <input
                                  type="text"
                                  className="lmb-input"
                                  placeholder="DD-MM-YYYY"
                                  value={toDisplay(dates?.plannedEndDate)}
                                  onChange={(e) => {
                                    let v = e.target.value.replace(/[^0-9-]/g, "");
                                    const digits = v.replace(/-/g, "");
                                    if (digits.length >= 4) v = digits.slice(0, 2) + "-" + digits.slice(2, 4) + "-" + digits.slice(4, 8);
                                    else if (digits.length >= 2) v = digits.slice(0, 2) + "-" + digits.slice(2);
                                    else v = digits;
                                    e.target.value = v;
                                    const iso = toISO(v);
                                    if (iso) updateStepDate(stepId, "plannedEndDate", iso);
                                    else if (!v) updateStepDate(stepId, "plannedEndDate", null);
                                  }}
                                  maxLength={10}
                                  disabled={isViewMode}
                                  style={{ fontSize: 13, padding: "4px 8px", width: 110 }}
                                />
                              ) : (
                                <span className="lmb-muted" style={{ fontSize: 12 }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center", padding: "6px 12px", fontSize: 13, color: "var(--text-muted)" }}>
                              {days !== null ? days : "—"}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {/* Footer: progress + save */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: "1px solid var(--border-default)",
                  }}
                >
                  <span className="lmb-muted" style={{ fontSize: 12 }}>
                    {getDateCount(activeBatchId)}/{totalSteps} steps have dates
                    {dirtyBatches.has(activeBatchId) && (
                      <span style={{ color: "var(--status-warning)", marginLeft: 8 }}>
                        (unsaved changes)
                      </span>
                    )}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {saveMessage && (
                      <span
                        style={{
                          fontSize: 12,
                          color: saveMessage.type === "success" ? "#059669" : "#dc2626",
                        }}
                      >
                        {saveMessage.text}
                      </span>
                    )}
                    {!isViewMode && module?.id && (
                      <button
                        className="lmb-btn lmb-btn-primary"
                        onClick={handleSave}
                        disabled={savingDates || !dirtyBatches.has(activeBatchId)}
                        type="button"
                      >
                        {savingDates ? "Saving..." : "Save Schedule"}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Empty state: no batches assigned */}
      {batchObjects.length === 0 && (
        <div className="lmb-card">
          <div className="lmb-card-bd" style={{ textAlign: "center", padding: "32px 16px", color: "var(--text-faint)" }}>
            No batches assigned yet. Use the search above to add batches to this lesson plan.
          </div>
        </div>
      )}

      {/* Empty state: no steps defined */}
      {batchObjects.length > 0 && allSteps.length === 0 && (
        <div className="lmb-card">
          <div className="lmb-card-bd" style={{ textAlign: "center", padding: "32px 16px", color: "var(--text-faint)" }}>
            No steps defined yet. Add chapters and steps in the Builder or Table View tab first.
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: shift a date string by N days
function shiftDate(dateStr: string | null, days: number): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
